import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../utils/api';
import { applyThemeScale } from '../utils/themeColor';
import { useAuth } from './AuthContext';

const SettingsContext = createContext(null);

/**
 * 默认设置。
 *
 * 提醒相关收敛成两个键：
 *   reminder_mode   —— 到点提醒用什么方式（见下方 REMINDER_MODES）
 *   reminder_repeat —— 没确认就再提醒一次。它管的是「要不要多喊几遍」，
 *                      和「用什么方式喊」是两件事，所以独立保留。
 *
 * 界面操作播报（voice_ui）也仍是独立开关：它跟吃药提醒无关，
 * 只是「点了按钮要不要念一句」，嫌吵可以关掉而不影响提醒。
 */
const DEFAULT_SETTINGS = {
  app_name: '吃药提醒',
  theme_color: '#4CAF50',
  dark_mode: false,
  font_size: 'large',
  reminder_mode: 'all',
  voice_ui: true,
  voice_speed: '1',
  emergency_contact_1: '',
  emergency_contact_2: '',
  user_name: '',
  reminder_repeat: true,
};

/**
 * 提醒方式。
 *
 * 原来的「到点语音播报 / 提示铃声 / 提醒振动」三个开关合并成这一个选择：
 * 三处开关用户很难判断谁管谁，而且「要铃声但不要语音」这类组合
 * 实际用三个互相独立的开关根本讲不清楚。
 * 顺序即界面上的展示顺序，默认项放第一位。
 */
/**
 * 到点提醒方式。
 *
 * 三个字段各管一件事，别混用：
 *   short —— 设置页按钮上的字。5 个选项要并排挤进一行，所以必须短到 2 个字：
 *            手机开「超大号」字号时一个字 20px，一行 5 个格子每格只剩约 60px 宽，
 *            写 4 个字（80px）就会顶破格子。
 *   label —— 完整说法，用于语音播报（「提醒方式已改为只念语音」），可以长。
 *   hint  —— 解释，折进 InfoTip 气泡里，不占屏幕。
 */
export const REMINDER_MODES = [
  { value: 'all', short: '全部', label: '语音 + 铃声 + 振动', hint: '三种一起来，最不容易错过' },
  { value: 'voice', short: '语音', label: '只念语音', hint: '只用说话的方式提醒，安静环境也听得见' },
  { value: 'sound', short: '铃声', label: '只响铃声', hint: '只响提示音，不说话' },
  { value: 'vibrate', short: '振动', label: '只振动', hint: '只让手机振动（iPhone 的浏览器不支持这一项）' },
  { value: 'silent', short: '静音', label: '静音', hint: '不发声也不振动，只在屏幕上弹提醒' },
];

const MODE_VALUES = REMINDER_MODES.map(m => m.value);

/** 需要从「服务端字符串」转成布尔值的键（含历史遗留键，供迁移读取） */
const BOOLEAN_KEYS = [
  'dark_mode',
  'voice_ui',
  'voice_broadcast',
  'voice_announce',
  'reminder_sound',
  'reminder_vibrate',
  'reminder_repeat',
];

const toBoolean = v => (typeof v === 'string' ? v === 'true' : !!v);

/** 已废弃的键：只用于读取旧数据，不再写回服务端 */
const LEGACY_KEYS = ['voice_broadcast', 'voice_announce', 'reminder_sound', 'reminder_vibrate'];

/**
 * 旧数据迁移：把历史上拆出去的开关合回一个提醒方式。
 *
 * 这几个键改过两轮 —— voice_broadcast（单一语音开关）
 * → voice_announce + voice_ui → 现在语音播报又和铃声、振动合并成 reminder_mode。
 * 逐级按「旧键在不在」判断，保证老用户升级后设置被保留、而不是悄悄重置成默认值。
 */
function migrateReminderMode(source) {
  if (typeof source.reminder_mode === 'string' && MODE_VALUES.indexOf(source.reminder_mode) >= 0) {
    return source.reminder_mode;
  }
  if (!LEGACY_KEYS.some(k => k in source)) {
    return DEFAULT_SETTINGS.reminder_mode;
  }

  const voice = 'voice_announce' in source
    ? toBoolean(source.voice_announce)
    : ('voice_broadcast' in source ? toBoolean(source.voice_broadcast) : true);
  const sound = 'reminder_sound' in source ? toBoolean(source.reminder_sound) : true;
  const vibrate = 'reminder_vibrate' in source ? toBoolean(source.reminder_vibrate) : true;

  const onCount = [voice, sound, vibrate].filter(Boolean).length;
  if (onCount === 3) return 'all';
  if (onCount === 0) return 'silent';
  if (voice && !sound && !vibrate) return 'voice';
  if (!voice && sound && !vibrate) return 'sound';
  if (!voice && !sound && vibrate) return 'vibrate';
  // 剩下的组合（如「铃声 + 振动」）五档里没有精确对应，取覆盖面最广的一档
  return 'all';
}

/**
 * 归一服务端返回的设置。
 *
 * 服务端 settings 表是 key-value 结构，值统一以字符串存储，
 * 因此需要按类型还原，并把历史的三个提醒开关迁移成 reminder_mode。
 */
function normalizeSettings(raw) {
  const source = raw || {};
  const normalized = { ...source };

  for (const key of BOOLEAN_KEYS) {
    if (key in normalized) normalized[key] = toBoolean(normalized[key]);
  }

  normalized.reminder_mode = migrateReminderMode(source);

  for (const key of LEGACY_KEYS) delete normalized[key];

  return { ...DEFAULT_SETTINGS, ...normalized };
}

export function SettingsProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const userId = user ? user.id : null;

  /**
   * 拉取设置必须等鉴权结果出来之后再执行。
   *
   * 原实现是「挂载即请求」，而挂载时 App 还没有 token —— 请求被判 401 后静默降级为默认值，
   * 于是登录成功后用户自己的主题、字体、提醒方式全都不会生效，必须刷新页面才恢复。
   */
  useEffect(() => {
    if (authLoading) return;
    if (!userId) {
      setSettings(DEFAULT_SETTINGS);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api.getSettings()
      .then(data => {
        if (cancelled) return;
        setSettings(normalizeSettings(data));
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [authLoading, userId]);

  useEffect(() => {
    const root = document.documentElement;
    // 整套色阶都由主题色派生（浅色档做底、深色档做字），
    // 避免 --primary-light 停留在默认绿、与用户选的主题色打架。
    applyThemeScale(settings.theme_color, root);
    // 夜间模式：切换 data-theme 属性
    if (settings.dark_mode) {
      root.setAttribute('data-theme', 'dark');
    } else {
      root.removeAttribute('data-theme');
    }
    const sizeMap = { small: '16px', medium: '20px', large: '24px', xlarge: '28px' };
    const largeMap = { small: '20px', medium: '24px', large: '28px', xlarge: '32px' };
    const smallMap = { small: '13px', medium: '16px', large: '18px', xlarge: '20px' };
    root.style.setProperty('--font-size-base', sizeMap[settings.font_size] || '20px');
    root.style.setProperty('--font-size-large', largeMap[settings.font_size] || '24px');
    root.style.setProperty('--font-size-small', smallMap[settings.font_size] || '16px');
    document.title = settings.app_name;
  }, [settings]);

  const updateSettings = async (newSettings) => {
    // 不把已废弃的旧键写回服务端
    const payload = { ...newSettings };
    for (const key of LEGACY_KEYS) delete payload[key];
    const { voice_broadcast, ...rest } = payload;
    void voice_broadcast;
    const updated = { ...settings, ...rest };
    setSettings(updated);
    await api.updateSettings(rest);
  };

  return (
    <SettingsContext.Provider value={{ settings, updateSettings, loading }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useSettings must be used within SettingsProvider');
  return ctx;
}
